import { currency2buffer, isEthereumLikeToken } from '@exodus/ethereum-lib'
import { bufferToHex, toBuffer } from '@exodus/ethereumjs/util'
import BN from 'bn.js'

import { estimateGas, isContractAddressCached } from './eth-like-util.js'

export const EXTRA_PERCENTAGE = 20

// 16 gas per non-zero byte (4 gas per zero byte) of "transaction input data"
const GAS_PER_NON_ZERO_BYTE = 16

export const DEFAULT_CONTRACT_GAS_LIMIT = 1e6

// Starting with geth v1.9.14, if gasPrice is set for eth_estimateGas call, the call allowance will
// be calculated with account's balance divided by gasPrice. If user's balance is too low,
// the gasEstimation will fail. If gasPrice is set to '0x0', the account's balance is not
// used to estimate gas.
export async function estimateGasLimit(
  asset,
  fromAddress,
  toAddress,
  amount,
  data,
  gasPrice = '0x',
  extraPercentage = EXTRA_PERCENTAGE
) {
  const opts = {
    from: fromAddress,
    to: toAddress,
    value: normalizeAmount(amount),
    data: Buffer.isBuffer(data) ? bufferToHex(data) : data,
    gasPrice: normalizeGasPrice(gasPrice),
  }

  const estimatedGas = await estimateGas({ asset, ...opts })
  return new BN(estimatedGas.slice(2), 16)
    .imuln(100 + extraPercentage)
    .idivn(100)
    .toNumber()
}

export function resolveDefaultTxInput({ asset, toAddress, amount }) {
  return isEthereumLikeToken(asset)
    ? bufferToHex(asset.contract.transfer.build(toAddress.toLowerCase(), amount.toBaseString()))
    : '0x'
}

export async function fetchGasLimit({
  asset,
  fromAddress,
  toAddress,
  txInput: providedTxInput,
  amount,
  bip70,
  throwOnError = true,
  extraPercentage,
}) {
  if (bip70?.bitpay?.data && bip70?.bitpay?.gasPrice)
    return asset.name === 'ethereum' ? 65_000 : 130_000 // from on chain stats https://dune.xyz/queries/189123

  if (!amount) amount = asset.currency.ZERO

  const txInput = providedTxInput || resolveDefaultTxInput({ asset, toAddress, amount })

  const defaultGasLimit = () => asset.gasLimit + GAS_PER_NON_ZERO_BYTE * toBuffer(txInput).length

  const isContract = await isContractAddressCached({ asset, address: toAddress })

  if (isEthereumLikeToken(asset)) {
    amount = asset.baseAsset.currency.ZERO
    toAddress = asset.contract.address
  } else if (
    !isContract &&
    !asset.forceGasLimitEstimation &&
    ![
      // back compatiblity until all plugins are updated. Then remove this array
      'ethereumarbone',
      'ethereumarbonesepolia',
      'ethereumarbnova',
      'mantle',
      'mantlesepolia',
    ].includes(asset.name)
  ) {
    return defaultGasLimit()
  }

  // calling forwarder contracts with a bumped gas limit causes 'Out Of Gas' error on chain
  // Since geth v1.9.14 estimateGas will throw if user does not have enough ETH.
  // If gasPrice is set to zero, estimateGas will make the expected estimation.
  const gasPrice = bufferToHex(currency2buffer(asset.baseAsset.currency.ZERO))

  try {
    return await estimateGasLimit(
      asset,
      fromAddress,
      toAddress,
      amount,
      txInput,
      gasPrice,
      extraPercentage
    )
  } catch (err) {
    if (throwOnError) throw err

    console.error('fetchGasLimit error', err)

    // fallback value for contract case
    if (isContract) return asset.contractGasLimit || DEFAULT_CONTRACT_GAS_LIMIT

    // fallback value for rest cases: token.
    return defaultGasLimit()
  }
}

function normalizeAmount(amount) {
  if (!Buffer.isBuffer(amount)) {
    amount = currency2buffer(amount)
  }

  amount = bufferToHex(amount)
  while (amount[2] === '0') amount = '0x' + amount.slice(3)
  if (amount === '0x') amount = '0x0'

  return amount
}

function normalizeGasPrice(gasPrice) {
  while (gasPrice[2] === '0') gasPrice = '0x' + gasPrice.slice(3)
  if (gasPrice === '0x') gasPrice = '0x0'
  return gasPrice
}
